import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL, SITE_URL, LOGIN_ADMIN_ONLY, HIKE_PAGE_ONLY } from '../config';
import Spinner from '../components/Spinner';
import DynamicForm from '../components/DynamicForm';
import FormResponsesModal from '../components/FormResponsesModal';
import ExternalParticipantModal from '../components/ExternalParticipantModal';
import PhotoCarousel from '../components/PhotoCarousel';
import WindyWeatherWidget from '../components/WindyWeatherWidget';
import Footer from '../components/Footer';
import colors from '../theme/colors';

export default function HikeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, registerForHike, unregisterFromHike, toggleFavorite, isFavorite, getParticipationStatus, hasPermission } = useAuth();
  const [hike, setHike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [canManageHike, setCanManageHike] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnregisterConfirm, setShowUnregisterConfirm] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [schedaVisibilityLoading, setSchedaVisibilityLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [registrationClosedLoading, setRegistrationClosedLoading] = useState(false);
  const [participantLoadingStates, setParticipantLoadingStates] = useState({});
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [selectedParticipantForResponses, setSelectedParticipantForResponses] = useState(null);
  const [responseModalApproveLoading, setResponseModalApproveLoading] = useState(false);
  const [responseModalRejectLoading, setResponseModalRejectLoading] = useState(false);
  // External participant modal states
  const [showExternalParticipantModal, setShowExternalParticipantModal] = useState(false);
  const [externalParticipantModalMode, setExternalParticipantModalMode] = useState('add');
  const [selectedExternalParticipant, setSelectedExternalParticipant] = useState(null);
  const [externalParticipantLoading, setExternalParticipantLoading] = useState(false);
  const [deleteExternalLoading, setDeleteExternalLoading] = useState({});
  const [exportLoading, setExportLoading] = useState(null); // 'csv' | 'pdf' | null

  // Context menu state for Gestione section
  const [showGestioneMenu, setShowGestioneMenu] = useState(false);
  const [gestioneMenuPosition, setGestioneMenuPosition] = useState({ top: 0, left: 0 });
  const gestioneMenuRef = useRef(null);
  const [embedCopied, setEmbedCopied] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (gestioneMenuRef.current && !gestioneMenuRef.current.contains(event.target)) {
        setShowGestioneMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openGestioneMenu = (event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setGestioneMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - 200 // 200px = menu width
    });
    setShowGestioneMenu(!showGestioneMenu);
  };

  const handleCopyEmbedCode = () => {
    const embedUrl = `${SITE_URL}/embed/hike?hikeId=${id}`;
    const iframeCode = `<iframe src="${embedUrl}" width="100%" height="120" frameborder="0" style="border: 1px solid #e5e7eb; border-radius: 8px;"></iframe>`;
    navigator.clipboard.writeText(iframeCode).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
      setShowGestioneMenu(false);
    });
  };

  const handleCopyEmbedPageCode = () => {
    const embedUrl = `${SITE_URL}/embed/hike-page?hikeId=${id}`;
    const iframeCode = `<iframe src="${embedUrl}" width="100%" height="1200" frameborder="0" style="border: 1px solid #e5e7eb; border-radius: 8px;"></iframe>`;
    navigator.clipboard.writeText(iframeCode).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
      setShowGestioneMenu(false);
    });
  };

  // Use enriched hike data from backend, fallback to AuthContext cache
  const isFavorited = hike?.is_favorited !== undefined ? hike.is_favorited : isFavorite(parseInt(id));
  const participationStatus = hike?.participation_status !== undefined
    ? (hike.participation_status?.registered ? hike.participation_status : null)
    : (getParticipationStatus(parseInt(id)) ? { registered: true, stato: getParticipationStatus(parseInt(id)) } : null);
  const participants = hike?.participants || [];
  const loadingParticipants = false; // Participants loaded with hike data

  const fetchHikeDetails = useCallback(async () => {
    try {
      // Single API call - backend enriches response with user context if authenticated
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/hikes/${id}`, { headers });

      if (!response.ok) {
        throw new Error('Gita non trovata');
      }

      const data = await response.json();
      setHike(data);
      // Backend already included is_favorited, participation_status, and participants

      // Check if current user is an organizer of this hike
      if (user && data.organizer_ids.includes(user.id)) {
        setIsOrganizer(true);
        setCanManageHike(true);
      } else if (user && hasPermission('hike:edit:any')) {
        // Admins can manage all hikes, but aren't automatic participants
        setCanManageHike(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, user, token]);

  useEffect(() => {
    fetchHikeDetails();
  }, [fetchHikeDetails]);

  const handleFavoriteToggle = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setFavoriteLoading(true);
    try {
      await toggleFavorite(parseInt(id));
      // Refetch to get updated data
      await fetchHikeDetails();
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const fetchParticipants = async () => {
    // Refetch hike to get updated participants list
    await fetchHikeDetails();
  };

  const handleRegister = async () => {
    // If user is already registered, show confirmation dialog
    if (participationStatus?.registered) {
      setShowUnregisterConfirm(true);
      return;
    }

    // If hike has a registration form, show it first
    if (hike.has_registration_form && hike.registration_form_schema?.length > 0) {
      setShowRegistrationForm(true);
      return;
    }

    // Otherwise, proceed with registration directly
    await performRegistration();
  };

  const performRegistration = async (formResponses = null) => {
    setRegisterLoading(true);
    setError(''); // Clear any previous errors
    try {
      await registerForHike(hike.id, formResponses);
      // Refetch to get updated participation status from backend
      await fetchHikeDetails();
      // Close the form modal if it was open
      setShowRegistrationForm(false);
    } catch (err) {
      setError(err.message);
      // On error, refetch to ensure UI is in sync with backend
      await fetchHikeDetails();
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleUnregister = async () => {
    setShowUnregisterConfirm(false);
    setRegisterLoading(true);
    setError(''); // Clear any previous errors
    try {
      await unregisterFromHike(hike.id);
      // Refetch to get updated participation status from backend
      await fetchHikeDetails();
    } catch (err) {
      setError(err.message);
      // On error, refetch to ensure UI is in sync with backend
      await fetchHikeDetails();
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'eliminazione');
      }

      navigate('/');
    } catch (err) {
      setError(err.message);
      setShowDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setParticipantLoadingStates(prev => ({ ...prev, [`approve-${userId}`]: true }));
    try {
      const url = `${API_URL}/hikes/${id}/approve/${userId}`;
      console.log('Approving request:', { url, userId });
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Approve failed:', data);
        throw new Error(data.detail || 'Errore durante l\'approvazione');
      }

      await fetchParticipants();
    } catch (err) {
      setError(err.message);
    } finally {
      setParticipantLoadingStates(prev => ({ ...prev, [`approve-${userId}`]: false }));
    }
  };

  const handleReject = async (userId) => {
    setParticipantLoadingStates(prev => ({ ...prev, [`reject-${userId}`]: true }));
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/reject/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il rifiuto');
      }

      await fetchParticipants();
    } catch (err) {
      setError(err.message);
    } finally {
      setParticipantLoadingStates(prev => ({ ...prev, [`reject-${userId}`]: false }));
    }
  };

  const handleApproveExternal = async (participationId) => {
    setParticipantLoadingStates(prev => ({ ...prev, [`approve-${participationId}`]: true }));
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/approve-external/${participationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'approvazione');
      }

      await fetchParticipants();
    } catch (err) {
      setError(err.message);
    } finally {
      setParticipantLoadingStates(prev => ({ ...prev, [`approve-${participationId}`]: false }));
    }
  };

  const handleRejectExternal = async (participationId) => {
    setParticipantLoadingStates(prev => ({ ...prev, [`reject-${participationId}`]: true }));
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/reject-external/${participationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il rifiuto');
      }

      await fetchParticipants();
    } catch (err) {
      setError(err.message);
    } finally {
      setParticipantLoadingStates(prev => ({ ...prev, [`reject-${participationId}`]: false }));
    }
  };

  // Handlers for approve/reject from the FormResponsesModal
  const handleModalApprove = async (userId) => {
    setResponseModalApproveLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/approve/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'approvazione');
      }

      await fetchParticipants();
      setSelectedParticipantForResponses(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setResponseModalApproveLoading(false);
    }
  };

  const handleModalReject = async (userId) => {
    setResponseModalRejectLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/reject/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il rifiuto');
      }

      await fetchParticipants();
      setSelectedParticipantForResponses(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setResponseModalRejectLoading(false);
    }
  };

  const handleModalApproveExternal = async (participationId) => {
    setResponseModalApproveLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/approve-external/${participationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'approvazione');
      }

      await fetchParticipants();
      setSelectedParticipantForResponses(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setResponseModalApproveLoading(false);
    }
  };

  const handleModalRejectExternal = async (participationId) => {
    setResponseModalRejectLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/reject-external/${participationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il rifiuto');
      }

      await fetchParticipants();
      setSelectedParticipantForResponses(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setResponseModalRejectLoading(false);
    }
  };

  // External participant handlers
  const handleAddExternalParticipant = () => {
    setExternalParticipantModalMode('add');
    setSelectedExternalParticipant(null);
    setShowExternalParticipantModal(true);
  };

  const handleEditExternalParticipant = (participant) => {
    setExternalParticipantModalMode('edit');
    setSelectedExternalParticipant(participant);
    setShowExternalParticipantModal(true);
  };

  const handleExternalParticipantSubmit = async (data) => {
    setExternalParticipantLoading(true);
    try {
      const url = externalParticipantModalMode === 'edit'
        ? `${API_URL}/hikes/${id}/external-participants/${selectedExternalParticipant.id}`
        : `${API_URL}/hikes/${id}/external-participants`;

      const response = await fetch(url, {
        method: externalParticipantModalMode === 'edit' ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante il salvataggio');
      }

      await fetchParticipants();
      setShowExternalParticipantModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setExternalParticipantLoading(false);
    }
  };

  const handleDeleteExternalParticipant = async (participantId) => {
    setDeleteExternalLoading(prev => ({ ...prev, [participantId]: true }));
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/external-participants/${participantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante l\'eliminazione');
      }

      await fetchParticipants();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteExternalLoading(prev => ({ ...prev, [participantId]: false }));
    }
  };

  const handleToggleVisibility = async () => {
    setVisibilityLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/toggle-visibility`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il cambio visibilità');
      }

      await fetchHikeDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleToggleSchedaVisibility = async () => {
    setSchedaVisibilityLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/toggle-scheda-visibility`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il cambio visibilità scheda');
      }

      await fetchHikeDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setSchedaVisibilityLoading(false);
    }
  };

  const handleToggleRegistrationClosed = async () => {
    setRegistrationClosedLoading(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/toggle-registration-closed`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il cambio stato iscrizioni');
      }

      await fetchHikeDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistrationClosedLoading(false);
    }
  };

  const handleExportPersonalData = async () => {
    setExportLoading('personal');
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/export/personal-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'export dati anagrafici');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'dati_anagrafici.csv';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting personal data:', err);
      setError(err.message || 'Errore durante l\'export dati anagrafici');
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportPDF = async () => {
    setExportLoading('pdf');
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'export PDF');
      }

      // Create blob with explicit PDF type
      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'partecipanti.pdf';

      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError(err.message || 'Errore durante l\'export PDF');
    } finally {
      setExportLoading(null);
    }
  };

  const handleToggleCancelled = async () => {
    setCancelLoading(true);
    setShowCancelConfirm(false);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cancelled: !hike.is_cancelled })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante il cambio stato');
      }

      await fetchHikeDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleArchive = async () => {
    setArchiveLoading(true);
    setShowArchiveConfirm(false);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}/archive`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante l\'archiviazione');
      }

      // Redirect to archived hikes page after archiving
      navigate('/archived-hikes');
    } catch (err) {
      setError(err.message);
    } finally {
      setArchiveLoading(false);
    }
  };

  const getButtonConfig = () => {
    // If hike is cancelled, show disabled state
    if (hike?.is_cancelled) {
      return {
        text: 'Gita Annullata',
        color: 'text-white',
        customStyle: { backgroundColor: '#9CA3AF', cursor: 'not-allowed' },
        disabled: true,
        showMessage: false
      };
    }

    // Check registration closure status for non-registered users
    if (!participationStatus?.registered) {
      // Calculate registration status
      const approvedCount = participants.filter(p => p.stato === 'approvata').length;
      const isCapacityFull = hike?.max_partecipanti && approvedCount >= hike.max_partecipanti;
      const isDeadlinePassed = hike?.data_termine_iscrizioni && new Date(hike.data_termine_iscrizioni) < new Date();
      const isManuallyClosed = hike?.is_registration_closed;
      const isRegistrationNotOpened = hike?.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date();

      // Check if registration has not yet opened
      if (isRegistrationNotOpened) {
        return {
          text: `Iscrizioni dal ${formatDate(hike.data_apertura_iscrizioni)}`,
          color: 'text-white',
          customStyle: { backgroundColor: '#9CA3AF', cursor: 'not-allowed' },
          disabled: true,
          showMessage: false
        };
      }

      // Priority: capacity full > manually closed/deadline passed
      if (isCapacityFull) {
        return {
          showMessage: true,
          messageText: 'Posti esauriti',
          messageStyle: 'bg-green-100 border-green-300 text-green-800'
        };
      }
      if (isManuallyClosed || isDeadlinePassed) {
        return {
          showMessage: true,
          messageText: 'Iscrizioni chiuse',
          messageStyle: 'bg-gray-100 border-gray-300 text-gray-700'
        };
      }

      return {
        text: 'Iscriviti alla Gita',
        color: 'text-white',
        customStyle: { backgroundColor: '#0E60C6' },
        disabled: false,
        showMessage: false
      };
    }

    const stato = participationStatus.stato;

    if (stato === 'approvata') {
      return {
        text: '✓ Iscritto - Clicca per Annullare',
        color: 'bg-red-600 hover:bg-red-700',
        disabled: false,
        showMessage: false
      };
    } else if (stato === 'in_attesa') {
      return {
        text: '⏳ In Attesa di Approvazione\nClicca per Annullare',
        color: 'bg-yellow-500 hover:bg-yellow-600',
        disabled: false,
        showMessage: false
      };
    } else {
      return {
        text: '❌ Richiesta Non Approvata',
        color: 'bg-orange-600 hover:bg-orange-700',
        disabled: true,
        showMessage: false
      };
    }
  };

  const getTagColor = (tag) => {
    const tagColors = colors.tags[tag] || colors.tags.default;
    return `${tagColors.bg} ${tagColors.text}`;
  };

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const formatDate = (dateStr) => {
    return capitalizeFirst(new Date(dateStr).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).replace(', ', ' '));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="md" color="blue" />
          <div className="text-sm text-gray-600">Caricamento dettagli gita...</div>
        </div>
      </div>
    );
  }

  if (error || !hike) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-sm">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Errore</h2>
          <p className="text-sm text-gray-600 mb-4">{error || 'Gita non trovata'}</p>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 transition"
          >
            Torna Indietro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-6xl mx-auto px-4">
        {/* Back button - hidden when HIKE_PAGE_ONLY is true and user is not logged in */}
        {!HIKE_PAGE_ONLY || user ? (
          <button
            onClick={() => navigate(-1)}
            className="mb-3 text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            ← Indietro
          </button>
        ) : null}

        {/* Main 2-column layout */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Column - Main Content */}
          <div className="flex-1 lg:min-w-0">
            {/* Hero image */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4">
              <div className="relative">
                {hike.photos && hike.photos.length > 0 ? (
                  <PhotoCarousel
                    photos={hike.photos}
                    className="w-full h-48 lg:h-64"
                  />
                ) : hike.url_foto && hike.url_foto.trim() !== '' ? (
                  <img
                    src={hike.url_foto}
                    alt={hike.titolo}
                    className="w-full h-48 lg:h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 lg:h-64 bg-gray-100 flex items-center justify-center">
                    <p className="text-gray-400 text-sm">Nessuna foto</p>
                  </div>
                )}

                {/* Badges overlay */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {hike.is_cancelled && (
                    <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white shadow">
                      Annullata
                    </span>
                  )}
                  {hike.is_hidden && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-700 text-white shadow">
                      Nascosta
                    </span>
                  )}
                  {hike.is_archived && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white shadow">
                      Archiviata
                    </span>
                  )}
                </div>
              </div>

              {/* Title & basic info */}
              <div className="p-4">
                <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-1">{hike.titolo}</h1>

                {hike.organizers && hike.organizers.length > 0 && (
                  <p className="text-sm text-gray-500 mb-2">
                    {hike.organizers.map(org => org.display_name).join(', ')}
                  </p>
                )}

                {/* Tags */}
                {hike.tags && hike.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {hike.tags.map((tag, index) => (
                      <span
                        key={index}
                        className={`px-2 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description */}
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {hike.descrizione}
                </div>
              </div>
            </div>

            {/* Cost breakdown - separate card */}
            {(hike.quota_cai || hike.quota_assicurazione || hike.quota_pernotto) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <h2 className="text-base font-semibold text-gray-800 mb-3">Quote</h2>
                {(hike.quota_assicurazione || hike.quota_pernotto_non_cai) ? (
                  <>
                    {/* Two-column layout with vertical separator */}
                    <div className="flex items-stretch">
                      {/* Left column - Soci CAI */}
                      <div className="flex-1 pr-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">Soci CAI</p>
                        <div className="flex flex-wrap gap-3 text-sm">
                          {hike.quota_cai && (
                            <div>
                              <p className="text-xs text-gray-500">Quota CAI</p>
                              <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                            </div>
                          )}
                          {hike.quota_pernotto && (
                            <div>
                              <p className="text-xs text-gray-500">Pernotto</p>
                              <p className="font-semibold text-blue-600">€{hike.quota_pernotto.toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">Totale Soci CAI</p>
                          <p className="text-lg font-bold text-blue-600">
                            €{((hike.quota_cai || 0) + (hike.quota_pernotto || 0)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {/* Vertical separator */}
                      <div className="w-px bg-gray-200"></div>
                      {/* Right column - Non Soci */}
                      <div className="flex-1 pl-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">Non Soci</p>
                        <div className="flex flex-wrap gap-3 text-sm">
                          {hike.quota_cai && (
                            <div>
                              <p className="text-xs text-gray-500">Quota CAI</p>
                              <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                            </div>
                          )}
                          {hike.quota_assicurazione && (
                            <div>
                              <p className="text-xs text-gray-500">Assicurazione</p>
                              <p className="font-semibold text-blue-600">€{hike.quota_assicurazione.toFixed(2)}</p>
                            </div>
                          )}
                          {(hike.quota_pernotto_non_cai || hike.quota_pernotto) && (
                            <div>
                              <p className="text-xs text-gray-500">Pernotto</p>
                              <p className="font-semibold text-blue-600">€{(hike.quota_pernotto_non_cai || hike.quota_pernotto).toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">Totale Non Soci</p>
                          <p className="text-lg font-bold text-blue-600">
                            €{((hike.quota_cai || 0) + (hike.quota_assicurazione || 0) + (hike.quota_pernotto_non_cai || hike.quota_pernotto || 0)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {hike.quota_cai && (
                        <div>
                          <p className="text-xs text-gray-500">Quota CAI</p>
                          <p className="font-semibold text-blue-600">€{hike.quota_cai.toFixed(2)}</p>
                        </div>
                      )}
                      {hike.quota_pernotto && (
                        <div>
                          <p className="text-xs text-gray-500">Pernotto</p>
                          <p className="font-semibold text-blue-600">€{hike.quota_pernotto.toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">Totale</p>
                      <p className="text-lg font-bold text-green-600">
                        €{((hike.quota_cai || 0) + (hike.quota_pernotto || 0)).toFixed(2)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Categories */}
            {hike.categories && hike.categories.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <h2 className="text-base font-semibold text-gray-800 mb-2">Categorie</h2>
                <div className="flex flex-wrap gap-1.5">
                  {hike.categories.map((category, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Weather Forecast Widget */}
            {hike.latitude && hike.longitude && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                <h2 className="text-base font-semibold text-gray-800 mb-2">Previsioni Meteo</h2>
                <div className="w-full" style={{ height: '187px' }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://embed.windy.com/embed.html?type=forecast&location=coordinates&detail=true&detailLat=${hike.latitude}&detailLon=${hike.longitude}&metricTemp=°C&metricRain=mm&metricWind=km/h`}
                    frameBorder="0"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    loading="lazy"
                    title="Windy Weather Forecast"
                  ></iframe>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:w-80 lg:flex-shrink-0">
            <div className="lg:sticky lg:top-4 space-y-3">
              {/* Quick Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                {/* Date & Location */}
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-1">
                    <span>📅</span>
                    <span>{formatDate(hike.data_inizio)}{hike.data_fine && ` - ${formatDate(hike.data_fine)}`}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>📍</span>
                    <span>{hike.luogo}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-100 text-xs">
                  {hike.data_apertura_iscrizioni && (
                    <div>
                      <p className="text-gray-500">Apertura iscrizioni</p>
                      <p className="font-medium text-gray-800">{formatDate(hike.data_apertura_iscrizioni)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Termine iscrizioni</p>
                    <p className="font-medium text-gray-800">{formatDate(hike.data_termine_iscrizioni)}</p>
                  </div>
                  {hike.max_partecipanti && (
                    <div>
                      <p className="text-gray-500">Max partecipanti</p>
                      <p className="font-medium text-gray-800">{hike.max_partecipanti}</p>
                    </div>
                  )}
                  {hike.metadata?.distanza && (
                    <div>
                      <p className="text-gray-500">Distanza</p>
                      <p className="font-medium text-gray-800">{hike.metadata.distanza}</p>
                    </div>
                  )}
                  {hike.metadata?.dislivello && (
                    <div>
                      <p className="text-gray-500">Dislivello</p>
                      <p className="font-medium text-gray-800">{hike.metadata.dislivello}</p>
                    </div>
                  )}
                </div>

                {/* Approval info
                {hike.richiede_approvazione && !participationStatus?.registered && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    La ichiede approvazione degli organizzatori
                  </div>
                )} */}

                {/* Approved message
                {participationStatus?.stato === 'approvata' && hike.richiede_approvazione && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                    ✓ Partecipazione approvata
                  </div>
                )} */}

                {/* Waiting message
                {participationStatus?.stato === 'in_attesa' && hike.richiede_approvazione && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    ⏳ In attesa di approvazione
                  </div>
                )} */}

                {/* Registration button */}
                {user ? (
                  !isOrganizer ? (
                    getButtonConfig().showMessage ? (
                      <div className={`p-3 border rounded text-sm text-center font-medium ${getButtonConfig().messageStyle}`}>
                        {getButtonConfig().messageText}
                      </div>
                    ) : (
                      <button
                        onClick={handleRegister}
                        disabled={registerLoading || getButtonConfig().disabled}
                        className={`w-full py-2.5 rounded text-sm font-semibold transition text-white whitespace-pre-line ${getButtonConfig().color} disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                        style={getButtonConfig().customStyle || {}}
                      >
                        {registerLoading && <Spinner size="sm" color="white" />}
                        {registerLoading ? (participationStatus?.registered ? 'Annullamento...' : 'Iscrizione...') : getButtonConfig().text}
                      </button>
                    )
                  ) : (
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 text-center">
                      Sei organizzatore di questa gita
                    </div>
                  )
                ) : (
                    <div className="text-center">
                      {(() => {
                        const isRegistrationNotOpened = hike?.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date();
                        if (isRegistrationNotOpened) {
                          return (
                            <div className="p-3 border rounded text-sm text-center font-medium bg-gray-100 border-gray-300 text-gray-700">
                              Iscrizioni dal {formatDate(hike.data_apertura_iscrizioni)}
                            </div>
                          );
                        }
                        return (
                          <>
                            <button
                              onClick={() => navigate(`/guest-register/${id}`)}
                              className="w-full py-2.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700 transition"
                            >
                              Iscriviti
                            </button>
                            {LOGIN_ADMIN_ONLY ? (
                        <p className="text-xs text-gray-600 mt-2">
                          oppure scrivi una mail a <a href="mailto:segreteriacaimodena@gmail.com" className="text-blue-600 underline">segreteriacaimodena@gmail.com</a>
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          oppure <a href="/login" className="text-blue-600 underline">accedi</a> per iscriverti con il tuo account
                        </p>
                      )}
                          </>
                        );
                      })()}
                    </div>
                )}

                {/* Secondary actions */}
                <div className="flex gap-2 mt-3">
                  {user && (
                    <button
                      onClick={handleFavoriteToggle}
                      disabled={favoriteLoading}
                      className={`flex-1 py-2 rounded text-xs font-medium transition flex items-center justify-center gap-1 ${
                        isFavorited
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {favoriteLoading ? <Spinner size="sm" color="gray" /> : (isFavorited ? '⭐' : '☆')}
                      <span>{isFavorited ? 'Preferito' : 'Preferiti'}</span>
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      setCalendarLoading(true);
                      try {
                        const hikeResponse = await fetch(`${API_URL}/hikes/${id}`);
                        if (!hikeResponse.ok) throw new Error('Failed to get hike data');
                        const hikeData = await hikeResponse.json();
                        const sanitizeFilename = (title) => title.replace(/[^\w\s-]/g, '').replace(/[\s]+/g, '_').replace(/^_|_$/g, '');
                        const filename = `${sanitizeFilename(hikeData.titolo)}.ics`;
                        const calendarResponse = await fetch(`${API_URL}/hikes/${id}/calendar`);
                        if (!calendarResponse.ok) throw new Error('Failed to download calendar');
                        const blob = await calendarResponse.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error('Error downloading calendar:', err);
                        setError('Failed to download calendar file');
                      } finally {
                        setCalendarLoading(false);
                      }
                    }}
                    disabled={calendarLoading}
                    className={`flex-1 px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded text-xs font-medium transition flex items-center justify-center gap-1.5 ${!user ? 'flex-grow' : ''}`}
                  >
                    {calendarLoading ? <Spinner size="sm" color="gray" /> : '📅'}
                    <span className="hidden sm:inline">Aggiungi al calendario</span>
                    <span className="sm:hidden">Calendario</span>
                  </button>
                </div>
              </div>

              {/* Admin/Organizer actions */}
              {canManageHike && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs font-medium text-gray-500">Gestione</p>
                    <button
                      onClick={openGestioneMenu}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500"
                      aria-label="Altre opzioni"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleToggleVisibility}
                      disabled={visibilityLoading}
                      className={`py-2 rounded text-xs font-medium transition flex items-center justify-center gap-1 ${
                        hike.is_hidden
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {visibilityLoading ? <Spinner size="sm" color="gray" /> : (hike.is_hidden ? '👁️' : '🔒')}
                      <span>{hike.is_hidden ? 'Pubblica' : 'Nascondi'}</span>
                    </button>
                    <button
                      onClick={handleToggleSchedaVisibility}
                      disabled={schedaVisibilityLoading}
                      className={`py-2 rounded text-xs font-medium transition flex items-center justify-center gap-1 ${
                        hike.is_scheda_hidden
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {schedaVisibilityLoading ? <Spinner size="sm" color="gray" /> : (hike.is_scheda_hidden ? '📄' : '🔐')}
                      <span>{hike.is_scheda_hidden ? 'Pubblica Scheda' : 'Nascondi Scheda'}</span>
                    </button>
                    <button
                      onClick={handleToggleRegistrationClosed}
                      disabled={registrationClosedLoading}
                      className={`py-2 rounded text-xs font-medium transition flex items-center justify-center gap-1 ${
                        hike.is_registration_closed
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                    >
                      {registrationClosedLoading ? <Spinner size="sm" color="gray" /> : (hike.is_registration_closed ? '📋' : '🔒')}
                      <span>{hike.is_registration_closed ? 'Riapri Iscrizioni' : 'Chiudi Iscrizioni'}</span>
                    </button>
                    <button
                      onClick={() => hike.is_cancelled ? handleToggleCancelled() : setShowCancelConfirm(true)}
                      disabled={cancelLoading}
                      className={`py-2 rounded text-xs font-medium transition flex items-center justify-center gap-1 ${
                        hike.is_cancelled
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      }`}
                    >
                      {cancelLoading ? <Spinner size="sm" color="gray" /> : (hike.is_cancelled ? '✓' : '✗')}
                      <span>{hike.is_cancelled ? 'Ripristina' : 'Annulla'}</span>
                    </button>
                    <button
                      onClick={() => navigate(`/edit-hike/${id}`)}
                      className="py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-xs font-medium transition"
                    >
                      ✏️ Modifica
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs font-medium transition"
                    >
                      🗑️ Elimina
                    </button>
                    {/* Archive button - only show if not already archived */}
                    {!hike.is_archived && (
                      <button
                        onClick={() => setShowArchiveConfirm(true)}
                        disabled={archiveLoading}
                        className="py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded text-xs font-medium transition flex items-center justify-center gap-1"
                      >
                        {archiveLoading ? <Spinner size="sm" color="gray" /> : '📦'}
                        <span>Archivia</span>
                      </button>
                    )}
                    {/* Riproponi Gita button - only show for archived hikes */}
                    {hike.is_archived && (
                      <button
                        onClick={() => navigate('/create-hike', {
                          state: {
                            cloneFrom: hike,
                            isReproposal: true
                          }
                        })}
                        className="py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded text-xs font-medium transition flex items-center justify-center gap-1"
                      >
                        🔄 <span>Riproponi Gita</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Windy Weather Widget - shows for hikes with coordinates within X days */}
              <WindyWeatherWidget
                latitude={hike.latitude}
                longitude={hike.longitude}
                dataInizio={hike.data_inizio}
              />
            </div>
          </div>
        </div>

        {/* Participation Requests - Only visible to organizers and admins */}
        {canManageHike && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-4">
            <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold">Partecipanti</h2>
                {participants.filter(p => p.stato === 'approvata').length > 0 && (
                  <div className="flex gap-1">
                    <button
                      onClick={handleExportPersonalData}
                      disabled={exportLoading}
                      className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-xs font-medium transition flex items-center gap-1 disabled:opacity-50"
                      title="Scarica dati anagrafici (CSV)"
                    >
                      {exportLoading === 'personal' ? <Spinner size="sm" color="white" /> : null}
                      Dati Anagrafici
                    </button>
                    <button
                      onClick={handleExportPDF}
                      disabled={exportLoading}
                      className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-xs font-medium transition flex items-center gap-1 disabled:opacity-50"
                      title="Scarica PDF"
                    >
                      {exportLoading === 'pdf' ? <Spinner size="sm" color="white" /> : null}
                      PDF
                    </button>
                  </div>
                )}
              </div>
              {hike.max_partecipanti && participants.length > 0 && (
                <div className="text-white/90 text-sm font-medium">
                  {participants.filter(p => p.stato === 'approvata').length}/{hike.max_partecipanti} iscritti
                </div>
              )}
            </div>
            <div className="p-4">
              {loadingParticipants ? (
                <div className="flex flex-col items-center gap-2 py-6">
                  <Spinner size="sm" color="blue" />
                  <div className="text-xs text-gray-500">Caricamento...</div>
                </div>
              ) : participants.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500 mb-3">Nessun partecipante</p>
                  <button
                    onClick={handleAddExternalParticipant}
                    className="w-full py-2 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition"
                  >
                    + Aggiungi Esterno
                  </button>
                </div>
              ) : (
                <>
                  {/* Warning if over capacity */}
                  {hike.max_partecipanti && participants.filter(p => p.stato === 'approvata').length > hike.max_partecipanti && (
                    <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                      ⚠️ Capacità superata: {participants.filter(p => p.stato === 'approvata').length}/{hike.max_partecipanti} iscritti
                    </div>
                  )}

                   <div className="space-y-2">
                     {participants.map((participant) => (
                       <div
                         key={participant.id}
                         className={`border rounded p-3 ${
                           participant.stato === 'approvata'
                             ? 'border-green-200 bg-green-50'
                             : participant.stato === 'in_attesa'
                             ? 'border-yellow-200 bg-yellow-50'
                             : participant.stato === 'annullata'
                             ? 'border-red-300 bg-red-100 opacity-60'
                             : 'border-red-200 bg-red-50'
                         }`}
                       >
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Name row */}
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {participant.is_external
                                  ? participant.external_name
                                  : `${participant.nome} ${participant.cognome}`}
                              </p>
                            {participant.is_external && participant.added_by_user_id && (
                                 <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-xs shrink-0">
                                   Esterno
                                 </span>
                               )}
                               {participant.is_external && !participant.added_by_user_id && (
                                 <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-xs shrink-0">
                                   Ospite
                                 </span>
                               )}
               <span className={`px-1.5 py-0.5 rounded text-xs shrink-0 ${
                                  participant.stato === 'approvata'
                                    ? 'bg-green-200 text-green-700'
                                    : participant.stato === 'in_attesa'
                                    ? 'bg-yellow-200 text-yellow-700'
                                    : participant.stato === 'annullata'
                                    ? 'bg-red-300 text-red-800'
                                    : 'bg-red-200 text-red-700'
                                }`}>
                                  {participant.stato === 'approvata' ? '✓ Approvato' : participant.stato === 'in_attesa' ? '⏳ In attesa' : participant.stato === 'annullata' ? '✕ Annullato' : '✗ Rifiutato'}
                                </span>
                            </div>

                            {/* Details row */}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                              <span className="truncate max-w-[180px]">
                                {participant.is_external
                                  ? (participant.external_email || '')
                                  : participant.email}
                              </span>
                              <span>
                                {participant.is_external
                                  ? (participant.external_phone || '')
                                  : (participant.numero_telefono || '')}
                              </span>
                              <span>
{participant.is_external
                                   ? (participant.is_cai_member == null ? 'N/D' : (participant.is_cai_member ? 'CAI' : 'Non CAI'))
                                   : (participant.iscritto_cai == null ? 'N/D' : (participant.iscritto_cai ? 'CAI' : 'Non CAI'))}
                              </span>
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-1 shrink-0">
                             {/* Form responses */}
                             {hike.has_registration_form && hike.registration_form_schema?.length > 0 && participant.form_responses && (
                               <button
                                 onClick={() => setSelectedParticipantForResponses(participant)}
                                 className="px-2 py-1 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded text-xs transition"
                                 title="Vedi risposte"
                               >
                                 📋 Risposte
                               </button>
                             )}

                           {/* External actions (only for organizer-added externals) */}
                              {participant.is_external && participant.added_by_user_id && (
                                <>
                                  <button
                                    onClick={() => handleEditExternalParticipant(participant)}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-xs transition"
                                    title="Modifica partecipante"
                                  >
                                    ✏️ Modifica
                                  </button>
                                  <button
                                    onClick={() => handleDeleteExternalParticipant(participant.id)}
                                    disabled={deleteExternalLoading[participant.id]}
                                    className="px-2 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition disabled:opacity-50"
                                    title="Rimuovi partecipante"
                                  >
                                    {deleteExternalLoading[participant.id] ? '...' : '🗑️ Rimuovi'}
                                  </button>
                                </>
                              )}

                            {/* Approval buttons (for registered users and guests) */}
                              {hike.richiede_approvazione && !participant.is_external && participant.stato === 'approvata' && (
                                  <button
                                    onClick={() => handleReject(participant.user_id)}
                                    disabled={participantLoadingStates[`reject-${participant.user_id}`]}
                                    className="px-2 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition disabled:opacity-50"
                                    title="Revoca approvazione"
                                  >
                                    {participantLoadingStates[`reject-${participant.user_id}`] ? '...' : 'Revoca'}
                                  </button>
                              )}
                              {hike.richiede_approvazione && !participant.is_external && participant.stato === 'in_attesa' && (
                                <>
                                  <button
                                    onClick={() => handleApprove(participant.user_id)}
                                    disabled={participantLoadingStates[`approve-${participant.user_id}`]}
                                    className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs transition disabled:opacity-50"
                                    title="Approva partecipazione"
                                  >
                                    {participantLoadingStates[`approve-${participant.user_id}`] ? '...' : 'Approva'}
                                  </button>
                                  <button
                                    onClick={() => handleReject(participant.user_id)}
                                    disabled={participantLoadingStates[`reject-${participant.user_id}`]}
                                    className="px-2 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition disabled:opacity-50"
                                    title="Rifiuta partecipazione"
                                  >
                                    {participantLoadingStates[`reject-${participant.user_id}`] ? '...' : 'Rifiuta'}
                                  </button>
                                </>
                              )}
                              {hike.richiede_approvazione && !participant.is_external && participant.stato !== 'approvata' && participant.stato !== 'in_attesa' && participant.stato !== 'annullata' && (
                                <button
                                  onClick={() => handleApprove(participant.user_id)}
                                  disabled={participantLoadingStates[`approve-${participant.user_id}`]}
                                  className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs transition disabled:opacity-50"
                                  title="Approva partecipazione"
                                >
                                  {participantLoadingStates[`approve-${participant.user_id}`] ? '...' : 'Approva'}
                                </button>
                              )}
                              {hike.richiede_approvazione && participant.is_external && !participant.added_by_user_id && participant.stato === 'approvata' && (
                                  <button
                                    onClick={() => handleRejectExternal(participant.id)}
                                    disabled={participantLoadingStates[`reject-${participant.id}`]}
                                    className="px-2 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition disabled:opacity-50"
                                    title="Revoca approvazione"
                                  >
                                    {participantLoadingStates[`reject-${participant.id}`] ? '...' : 'Revoca'}
                                  </button>
                              )}
                              {hike.richiede_approvazione && participant.is_external && !participant.added_by_user_id && participant.stato === 'in_attesa' && (
                                <>
                                  <button
                                    onClick={() => handleApproveExternal(participant.id)}
                                    disabled={participantLoadingStates[`approve-${participant.id}`]}
                                    className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs transition disabled:opacity-50"
                                    title="Approva partecipazione"
                                  >
                                    {participantLoadingStates[`approve-${participant.id}`] ? '...' : 'Approva'}
                                  </button>
                                  <button
                                    onClick={() => handleRejectExternal(participant.id)}
                                    disabled={participantLoadingStates[`reject-${participant.id}`]}
                                    className="px-2 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs transition disabled:opacity-50"
                                    title="Rifiuta partecipazione"
                                  >
                                    {participantLoadingStates[`reject-${participant.id}`] ? '...' : 'Rifiuta'}
                                  </button>
                                </>
                              )}
                              {hike.richiede_approvazione && participant.is_external && !participant.added_by_user_id && participant.stato !== 'approvata' && participant.stato !== 'in_attesa' && participant.stato !== 'annullata' && (
                                <button
                                  onClick={() => handleApproveExternal(participant.id)}
                                  disabled={participantLoadingStates[`approve-${participant.id}`]}
                                  className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-xs transition disabled:opacity-50"
                                  title="Approva partecipazione"
                                >
                                  {participantLoadingStates[`approve-${participant.id}`] ? '...' : 'Approva'}
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add External Participant button */}
                    <button
                      onClick={handleAddExternalParticipant}
                      className="w-full py-2 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition"
                    >
                      + Aggiungi Esterno
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Cancel Hike Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg p-5 max-w-sm w-full">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Annulla gita?</h3>
              <p className="text-sm text-gray-600 mb-1">{hike.titolo}</p>
              <p className="text-xs text-orange-600 mb-4">I partecipanti riceveranno una notifica.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelLoading}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:opacity-50"
                >
                  No
                </button>
                <button
                  onClick={handleToggleCancelled}
                  disabled={cancelLoading}
                  className="flex-1 px-3 py-2 bg-orange-600 text-white rounded text-sm font-medium hover:bg-orange-700 transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {cancelLoading && <Spinner size="sm" color="white" />}
                  Si
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg p-5 max-w-sm w-full">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Elimina gita?</h3>
              <p className="text-sm text-gray-600 mb-1">{hike.titolo}</p>
              <p className="text-xs text-red-600 mb-4">Questa azione non può essere annullata.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {deleteLoading && <Spinner size="sm" color="white" />}
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unregister Confirmation Modal */}
        {showUnregisterConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg p-5 max-w-sm w-full">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Rimuovi iscrizione?</h3>
              <p className="text-sm text-gray-600 mb-4">{hike.titolo}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowUnregisterConfirm(false)}
                  disabled={registerLoading}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:opacity-50"
                >
                  No
                </button>
                <button
                  onClick={handleUnregister}
                  disabled={registerLoading}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {registerLoading && <Spinner size="sm" color="white" />}
                  Sì, Rimuovi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg p-5 max-w-sm w-full">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Archivia gita?</h3>
              <p className="text-sm text-gray-600 mb-1">{hike.titolo}</p>
              <p className="text-xs text-gray-500 mb-4">La gita sarà spostata nell'archivio e non sarà più visibile agli utenti.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  disabled={archiveLoading}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleArchive}
                  disabled={archiveLoading}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {archiveLoading && <Spinner size="sm" color="white" />}
                  Archivia
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Registration Form Modal */}
        {showRegistrationForm && hike.registration_form_schema && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4">
                <h2 className="text-base font-semibold text-gray-800 mb-1">Modulo di Iscrizione</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Compila per iscriverti a <strong>{hike.titolo}</strong>
                </p>
                <DynamicForm
                  schema={hike.registration_form_schema}
                  onSubmit={performRegistration}
                  onCancel={() => setShowRegistrationForm(false)}
                  loading={registerLoading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Form Responses Modal */}
        {selectedParticipantForResponses && hike.registration_form_schema && (
          <FormResponsesModal
            participant={selectedParticipantForResponses}
            schema={hike.registration_form_schema}
            onApprove={() => selectedParticipantForResponses.is_external && !selectedParticipantForResponses.added_by_user_id
              ? handleModalApproveExternal(selectedParticipantForResponses.id)
              : handleModalApprove(selectedParticipantForResponses.user_id)}
            onReject={() => selectedParticipantForResponses.is_external && !selectedParticipantForResponses.added_by_user_id
              ? handleModalRejectExternal(selectedParticipantForResponses.id)
              : handleModalReject(selectedParticipantForResponses.user_id)}
            onClose={() => setSelectedParticipantForResponses(null)}
            approveLoading={responseModalApproveLoading}
            rejectLoading={responseModalRejectLoading}
          />
        )}

        {/* External Participant Modal */}
        <ExternalParticipantModal
          isOpen={showExternalParticipantModal}
          onClose={() => setShowExternalParticipantModal(false)}
          onSubmit={handleExternalParticipantSubmit}
          loading={externalParticipantLoading}
          mode={externalParticipantModalMode}
          participant={selectedExternalParticipant}
          formSchema={hike?.has_registration_form ? hike.registration_form_schema : null}
        />

        {/* Gestione Context Menu Portal */}
        {showGestioneMenu && createPortal(
          <div
            ref={gestioneMenuRef}
            className="fixed w-50 bg-white rounded-md shadow-lg border z-50"
            style={{ top: gestioneMenuPosition.top, left: gestioneMenuPosition.left }}
          >
            <div className="py-1">
              <button
                onClick={handleCopyEmbedCode}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {embedCopied ? 'Copiato!' : 'Copia codice embed (card)'}
              </button>
              <button
                onClick={handleCopyEmbedPageCode}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {embedCopied ? 'Copiato!' : 'Copia codice embed (pagina)'}
              </button>
            </div>
          </div>,
          document.body
        )}

        <Footer />
      </div>
    </div>
  );
}
